import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Alert,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Pagination,
  DialogActions,
  Avatar,
  MenuItem,
  Stack,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { API_DELETE_STUDENT, API_GET_ACADEMIC_SESSIONS, API_GET_STUDENT_DETAIL, API_GET_STUDENTS, API_PREVIEW_STUDENT_DOCUMENT, API_RESTORE_STUDENT, API_STATUS_UPDATE } from "../Config/ApiUrlConstant";
import { useAuth } from "../AuthContext";
import AddEditStudent from "./AddEditStudent";
import DialogContent from "@mui/material/DialogContent";
import { Dialog, DialogTitle } from "../Common/ModalDialog";
import { useSearchParams } from 'react-router-dom';
import StudentReportCards from './StudentReportCards';

const documentTitles = { photo: 'Student Photo', uid: 'Student UID Document', father_uid: "Father's UID Document", mother_uid: "Mother's UID Document" };
const DetailField = ({ label, value }) => <Grid item xs={12} sm={6} md={4}>
  <Typography variant="caption" color="text.secondary">{label}</Typography>
  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value === null || value === undefined || value === '' ? '—' : value}</Typography>
</Grid>;

const StudentManagement = () => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState({
    student_no: urlSearchParams.get('student_no') || '',
    student_name: urlSearchParams.get('student_name') || '',
    father_name: urlSearchParams.get('father_name') || '',
    class_name: urlSearchParams.get('class_name') || '',
    session: urlSearchParams.get('session') || '',
  });
  const [currentPage, setCurrentPage] = useState(Number(urlSearchParams.get('page')) || 1);
  const studentsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [showAllEnrollmentHistory, setShowAllEnrollmentHistory] = useState(false);
  const [studentToArchive, setStudentToArchive] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [studentStatusChange, setStudentStatusChange] = useState(null);
  const [showArchived, setShowArchived] = useState(urlSearchParams.get('archived') === 'true');
  const [studentToRestore, setStudentToRestore] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [academicSessions, setAcademicSessions] = useState([]);
  const isMobile = useMediaQuery('(max-width:700px)');

  const { logout, can, role, teacherAccess } = useAuth();
  const canCreateStudent = can('STUDENTS.CREATE'); const canEditStudent = can('STUDENTS.EDIT');
  const canArchiveStudent = can('STUDENTS.ARCHIVE'); const canRestoreStudent = can('STUDENTS.RESTORE');
  const unassignedTeacher = String(role || '').toUpperCase() === 'TEACHER' && !teacherAccess?.assignments?.length;

  // 🔹 Fetch students from API
  const getAllStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem("school_token");
      const response = await fetch(API_GET_STUDENTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...search, page: currentPage, page_size: studentsPerPage, archived: showArchived }),
      });

      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      const responseData = await response.json();
      const data = responseData.data || {};
      setStudents(data.records || []);
      setTotalPages(data.pagination?.total_pages || 0);
      setTotalRecords(data.pagination?.total_records || 0);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  }, [search, currentPage, showArchived, logout]);

  useEffect(() => {
    const timer = setTimeout(() => { void getAllStudents(); }, 300);
    return () => clearTimeout(timer);
  }, [getAllStudents]);

  useEffect(() => {
    fetch(API_GET_ACADEMIC_SESSIONS, { method: 'POST', headers: { 'Content-Type': 'application/json' } }).then((response) => response.json()).then((payload) => setAcademicSessions(payload.data || [])).catch(() => setAcademicSessions([]));
  }, []);

  useEffect(() => {
    const params = { ...search, page: String(currentPage) };
    if (showArchived) params.archived = 'true';
    Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
    setUrlSearchParams(params, { replace: true });
  }, [search, currentPage, showArchived, setUrlSearchParams]);

  useEffect(() => {
    let cancelled = false;
    const urls = [];
    Promise.all(students.filter((student) => student.student_photo_upload).map(async (student) => {
      const response = await fetch(API_PREVIEW_STUDENT_DOCUMENT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: student.id, document_type: 'photo' }) });
      if (!response.ok) return [student.id, null];
      const url = URL.createObjectURL(await response.blob()); urls.push(url); return [student.id, url];
    })).then((entries) => { if (!cancelled) setPhotoUrls(Object.fromEntries(entries.filter(([, url]) => url))); });
    return () => { cancelled = true; urls.forEach((url) => URL.revokeObjectURL(url)); };
  }, [students]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const currentStudents = students;

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleOpenDialog = (student = null) => {
    setStudentDetail(null);
    setEditStudent(student);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditStudent(null);
  };

  // Add inside StudentManagement component
  const handleToggleStatus = async (student) => {
    try {
      const token = localStorage.getItem("school_token");
      const response = await fetch(API_STATUS_UPDATE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: student.is_active === 1 ? 0 : 1, id: student.id }),
      });
      const result = await response.json();
      if (response.ok) {
        getAllStudents(); // refresh the list
      } else {
        setFeedback(result.message || "Failed to update status.");
      }
    } catch (error) {
      console.error("Error updating student status:", error);
    }
  };

  const clearFilters = () => {
    setSearch({ student_no: '', student_name: '', father_name: '', class_name: '', session: '' });
    setCurrentPage(1);
  };

  const showDetail = async (student) => {
    setOpenDialog(false); setEditStudent(null);
    const response = await fetch(API_GET_STUDENT_DETAIL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: student.id }) });
    const payload = await response.json();
    if (response.ok && payload.status) { setShowAllEnrollmentHistory(false); setStudentDetail(payload.data); } else setFeedback(payload.message || 'Unable to load student details.');
  };
  const previewDocument = async (student, documentType) => {
    try {
      const response = await fetch(API_PREVIEW_STUDENT_DOCUMENT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: student.id, document_type: documentType }) });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const mimeType = blob.type || response.headers.get('Content-Type') || '';
      const url = URL.createObjectURL(blob);
      setDocumentPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { url, type: documentType, mimeType, name: student.student_name };
      });
    } catch { setFeedback('Unable to open the requested document.'); }
  };
  const closeDocumentPreview = () => setDocumentPreview((current) => {
    if (current?.url) URL.revokeObjectURL(current.url);
    return null;
  });
  const archiveStudent = async () => {
    const response = await fetch(API_DELETE_STUDENT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: studentToArchive.id, reason: archiveReason.trim() }) });
    const payload = await response.json();
    if (response.ok && payload.status) { setStudentToArchive(null); setArchiveReason(''); getAllStudents(); } else setFeedback(payload.message || 'Unable to archive student.');
  };
  const restoreStudent = async () => {
    const response = await fetch(API_RESTORE_STUDENT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: studentToRestore.id }) });
    const payload = await response.json();
    if (response.ok && payload.status) { setStudentToRestore(null); void getAllStudents(); } else setFeedback(payload.message || 'Unable to restore student.');
  };

  return (
    <Box p={{ xs: 0, sm: 2 }}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5">Student List</Typography>
        </Grid>
        <Grid item xs={12} sm="auto">
          {(canArchiveStudent || canRestoreStudent || canCreateStudent) ? <Stack direction="row" spacing={1}>{(canArchiveStudent || canRestoreStudent) && <Button variant="outlined" onClick={() => { setShowArchived((current) => !current); setCurrentPage(1); }}>{showArchived ? 'Active Students' : 'Archived Students'}</Button>}{!showArchived && canCreateStudent ? <Button variant="contained" onClick={() => handleOpenDialog(null)}>Add New Student</Button> : null}</Stack> : null}
        </Grid>
      </Grid>

      {/* 🔹 Search Filters */}
      {unassignedTeacher && <Alert severity="info" sx={{ mb: 2 }}>No class or section is assigned to your Teacher profile. Ask an administrator to add an assignment under Administration → Teacher Management → Edit.</Alert>}

      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Student No"
            name="student_no"
            value={search.student_no}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}><TextField fullWidth select label="Academic Session" name="session" value={search.session} onChange={handleSearchChange} size="small"><MenuItem value="">All sessions</MenuItem>{academicSessions.map((session) => <MenuItem key={session.id} value={session.session_code}>{session.session_code}{session.is_active ? ' (Active)' : ''}</MenuItem>)}</TextField></Grid>
        <Grid item xs={12}><Button variant="outlined" onClick={clearFilters}>Clear Filters</Button></Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Student Name"
            name="student_name"
            value={search.student_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Father Name"
            name="father_name"
            value={search.father_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search by Class"
            name="class_name"
            value={search.class_name}
            onChange={handleSearchChange}
            size="small"
          />
        </Grid>
      </Grid>

      {/* 🔹 Student Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 3 }}>
        <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: isMobile ? 580 : 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Class</TableCell>
              {!isMobile ? <><TableCell>Father Name</TableCell><TableCell>Mother Name</TableCell><TableCell>DOB</TableCell><TableCell>UID</TableCell><TableCell>Mobile</TableCell><TableCell>Magic Charge</TableCell></> : null}
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentStudents.length > 0 ? (
              currentStudents.map((student, index) => (
                <TableRow key={student.id || index} hover onClick={() => showDetail(student)} sx={{ cursor: 'pointer' }} aria-label={`View details for ${student.student_name}`}>
                  <TableCell>{(currentPage - 1) * studentsPerPage + index + 1}</TableCell>
                  <TableCell><Stack direction="row" spacing={1} alignItems="center"><Avatar src={photoUrls[student.id]} alt={student.student_name} /><Box><Typography fontWeight={700}>{student.student_name}</Typography><Typography variant="caption" color="text.secondary">{student.student_no}</Typography></Box></Stack></TableCell>
                  <TableCell>{student.class}</TableCell>
                  {!isMobile ? <><TableCell>{student.student_father_name}</TableCell><TableCell>{student.student_mother_name}</TableCell><TableCell>{student.student_dob}</TableCell><TableCell>{student.student_uid}</TableCell><TableCell>{student.contact_no}</TableCell><TableCell>{student.magic_charge}</TableCell></> : null}
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    {student.is_active === 1 ? "Active" : "Inactive"}
                  </TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    {!showArchived ? <>{canEditStudent && <Button
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                      onClick={() => handleOpenDialog(student)}
                      style={{ width: "50px" }}
                    >
                      Edit
                    </Button>}
                    <Button size="small" variant="text" onClick={() => showDetail(student)}>View</Button>
                    {canArchiveStudent && <Button size="small" color="error" variant="text" onClick={() => { setArchiveReason(''); setStudentToArchive(student); }}>Archive</Button>}
                    {canEditStudent && <Button
                      size="small"
                      variant="outlined"
                      color={student.is_active === 1 ? "error" : "success"}
                      style={{ width: "100px" }}
                      onClick={() => setStudentStatusChange(student)}
                    >
                      {student.is_active === 1 ? "Deactivate" : "Activate"}
                    </Button>}</> : <><Button size="small" variant="text" onClick={() => showDetail(student)}>View</Button>{canRestoreStudent && <Button size="small" color="success" variant="outlined" onClick={() => setStudentToRestore(student)}>Restore</Button>}</>}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {unassignedTeacher ? 'No students are available until a class or section is assigned to your Teacher profile.' : 'No students found.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{totalRecords} student record(s) found</Typography>

      {/* 🔹 Pagination */}
      {totalPages > 1 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* 🔹 Add/Edit Student Dialog */}
      <Dialog open={(editStudent ? canEditStudent : canCreateStudent) && openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editStudent ? "Edit Student" : "Add Student"}</DialogTitle>
        <DialogContent dividers>
          <AddEditStudent
            studentData={editStudent}
            onSuccess={() => {
              getAllStudents();
              handleCloseDialog();
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(feedback)} onClose={() => setFeedback(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unable to update student status</DialogTitle>
        <DialogContent><Typography>{feedback}</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setFeedback(null)}>Close</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(studentDetail)} onClose={() => setStudentDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Student Details</DialogTitle>
        <DialogContent dividers>{studentDetail ? <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Stack alignItems="center">
              <Avatar src={photoUrls[studentDetail.id]} alt={studentDetail.student_name} sx={{ width: 100, height: 100 }} />
              {studentDetail.student_photo_upload ? <Button size="small" onClick={() => previewDocument(studentDetail, 'photo')}>View full photo</Button> : <Typography variant="caption">No photo uploaded</Typography>}
            </Stack>
            <Box><Typography variant="h5">{studentDetail.student_name}</Typography><Typography color="text.secondary">{studentDetail.student_no} · Class {studentDetail.class || '—'} · {studentDetail.session || 'Legacy session'}</Typography></Box>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Personal and contact information</Typography>
            <Grid container spacing={2}>
              <DetailField label="Student number" value={studentDetail.student_no} />
              <DetailField label="Date of birth" value={studentDetail.student_dob?.slice?.(0, 10)} />
              <DetailField label="Student UID" value={studentDetail.student_uid} />
              <DetailField label="Contact number" value={studentDetail.contact_no} />
              <DetailField label="Current class" value={studentDetail.class} />
              <DetailField label="Current session" value={studentDetail.session} />
              <DetailField label="Account status" value={studentDetail.is_deleted ? 'Archived' : Number(studentDetail.is_active) === 1 ? 'Active' : 'Inactive'} />
              <DetailField label="Magic charge" value={studentDetail.magic_charge} />
              <DetailField label="Relaxation" value={Number(studentDetail.is_relax) === 1 ? 'Yes' : 'No'} />
              <DetailField label="Created" value={studentDetail.created_at ? new Date(studentDetail.created_at).toLocaleString() : null} />
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Family and address</Typography>
            <Grid container spacing={2}>
              <DetailField label="Father's name" value={studentDetail.student_father_name} />
              <DetailField label="Mother's name" value={studentDetail.student_mother_name} />
              <DetailField label="Father's occupation" value={studentDetail.father_business} />
              <DetailField label="Address" value={studentDetail.student_address} />
              {studentDetail.is_deleted ? <DetailField label="Archive reason" value={studentDetail.delete_reason} /> : null}
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
            <Typography variant="h6">Private identity documents</Typography>
            <Typography variant="body2" color="text.secondary">Documents open through the protected preview service.</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {[
                ['uid', 'Student UID', studentDetail.student_uid_upload],
                ['father_uid', "Father's UID", studentDetail.father_uid_upload],
                ['mother_uid', "Mother's UID", studentDetail.mother_uid_upload],
              ].map(([type, label, available]) => available
                ? <Button key={type} variant="outlined" size="small" onClick={() => previewDocument(studentDetail, type)}>View {label}</Button>
                : <Typography key={type} variant="caption" color="text.secondary" sx={{ p: 1 }}>{label}: not uploaded</Typography>)}
            </Stack>
          </Paper>

          <Box sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}><Typography variant="h6">Academic Enrollment History</Typography>{studentDetail.can_view_historical_enrollment && <Button size="small" onClick={() => setShowAllEnrollmentHistory((current) => !current)}>{showAllEnrollmentHistory ? 'Current session only' : 'View previous sessions'}</Button>}</Stack>
            <TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Session</TableCell><TableCell>Class / Section</TableCell><TableCell>Roll no.</TableCell><TableCell>Status</TableCell><TableCell>Outcome</TableCell><TableCell>Enrolled</TableCell><TableCell>Exit</TableCell></TableRow></TableHead><TableBody>{(studentDetail.enrollment_history || []).filter((entry) => showAllEnrollmentHistory || entry.is_current_session).map((entry) => <TableRow key={`${entry.session_code}-${entry.class_name}`}><TableCell>{entry.session_code}{entry.is_current_session ? ' (Current)' : ''}</TableCell><TableCell>{entry.class_name}{entry.section_name ? ` / ${entry.section_name}` : ''}</TableCell><TableCell>{entry.roll_number || '—'}</TableCell><TableCell>{entry.enrollment_status}</TableCell><TableCell>{entry.promotion_outcome || '—'}</TableCell><TableCell>{entry.enrollment_date || '—'}</TableCell><TableCell>{entry.exit_date || '—'}</TableCell></TableRow>)}{!(studentDetail.enrollment_history || []).some((entry) => showAllEnrollmentHistory || entry.is_current_session) && <TableRow><TableCell colSpan={7} align="center">No enrollment history is available in this view.</TableCell></TableRow>}</TableBody></Table></TableContainer>
          </Box>
          {can('STUDENT_REPORT_CARDS.VIEW') && <StudentReportCards student={studentDetail} />}
        </Stack> : null}</DialogContent>
        <DialogActions><Button onClick={() => setStudentDetail(null)}>Close</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(documentPreview)} onClose={closeDocumentPreview} maxWidth="md" fullWidth><DialogTitle>{documentTitles[documentPreview?.type] || 'Student Document'}</DialogTitle><DialogContent key={`${documentPreview?.type}-${documentPreview?.url}`}>{documentPreview?.mimeType?.startsWith('image/') ? <img src={documentPreview.url} alt={`${documentTitles[documentPreview.type]} for ${documentPreview.name}`} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} /> : documentPreview?.mimeType === 'application/pdf' ? <iframe title={documentTitles[documentPreview?.type] || 'Student document preview'} src={documentPreview?.url} style={{ width: '100%', height: '70vh', border: 0 }} /> : <Alert severity="error">This document format cannot be previewed.</Alert>}</DialogContent><DialogActions><Button onClick={closeDocumentPreview}>Close</Button></DialogActions></Dialog>
      <Dialog open={Boolean(studentToArchive)} onClose={() => setStudentToArchive(null)} maxWidth="xs" fullWidth><DialogTitle>Archive student?</DialogTitle><DialogContent><Typography sx={{ mb: 2 }}>This keeps the student and fee history, but removes the student from active lists.</Typography><TextField autoFocus required fullWidth multiline minRows={2} label="Archive reason" value={archiveReason} onChange={(event) => setArchiveReason(event.target.value)} helperText="Minimum 3 characters" /></DialogContent><DialogActions><Button onClick={() => setStudentToArchive(null)}>Cancel</Button><Button color="error" variant="contained" disabled={archiveReason.trim().length < 3} onClick={archiveStudent}>Archive</Button></DialogActions></Dialog>
      <Dialog open={Boolean(studentStatusChange)} onClose={() => setStudentStatusChange(null)} maxWidth="xs" fullWidth><DialogTitle>Confirm status change</DialogTitle><DialogContent><Typography>Are you sure you want to {studentStatusChange?.is_active === 1 ? 'deactivate' : 'activate'} {studentStatusChange?.student_name}?</Typography></DialogContent><DialogActions><Button onClick={() => setStudentStatusChange(null)}>Cancel</Button><Button variant="contained" onClick={() => { handleToggleStatus(studentStatusChange); setStudentStatusChange(null); }}>Confirm</Button></DialogActions></Dialog>
      <Dialog open={Boolean(studentToRestore)} onClose={() => setStudentToRestore(null)} maxWidth="xs" fullWidth><DialogTitle>Restore student?</DialogTitle><DialogContent><Typography>Restore {studentToRestore?.student_name} to active student lists? Historical records will remain unchanged.</Typography></DialogContent><DialogActions><Button onClick={() => setStudentToRestore(null)}>Cancel</Button><Button color="success" variant="contained" onClick={restoreStudent}>Restore</Button></DialogActions></Dialog>
    </Box>
  );
};

export default StudentManagement;
